import { createNamespacedHelpers, mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import classNameSelect from './ClassNameSelect.vue';
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import { PAGINATION_DEFAULT, SEARCH_PAGE_LIMIT } from '@/constants/pagination';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';

import expressionsList from './ExpressionsList/ExpressionsList.vue';
import intentActions from './Actions/Actions.vue';
import intentChannelActions from './ChannelActions/ChannelActions.vue';
import entitiesList from './EntitiesList/EntitiesList.vue';
import { basicInputForm, buildingKitAiTesting } from '../Partials';
import MiddlePaneHeader from '../Partials/IQToolMiddlePaneHeader/IQToolMiddlePaneHeader.vue';
import ActionContainer from '../Partials/ActionContainer/ActionContainer.vue';
import {
  appendUnitType,
  confirmUnsavedWarning,
} from '@/libs';
import { STATUS_CODES } from '@/constants';
import { Events } from '@/libs';

const { mapGetters, mapMutations } = createNamespacedHelpers('intents/formIntent');
const STATUS_OPTIONS = {
  ALL: 'All',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

export default {
  name: 'intents',
  components: {
    classNameSelect,
    entitiesList,
    expressionsList,
    intentActions,
    intentChannelActions,
    basicInputForm,
    buildingKitAiTesting,
    MiddlePaneHeader,
    ActionContainer,
    ManagementPage,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    const intentNameRegex = helpers.regex(/^[a-zA-Z_][a-zA-Z.0-9\s_]*$/);
    return {
      newIntentName: { required, intentNameRegex },
    };
  },
  data() {
    return {
      search: '',
      newIntentName: '',
      conditionals: {
        conditional_states: [],
        conditional_else_states: [],
        use_conditional_actions: false,
      },
      filterStatus: STATUS_OPTIONS.ALL,
      loading: true,
      searchPageSize: SEARCH_PAGE_LIMIT,
      pagination: PAGINATION_DEFAULT,
      showAddIntentDialog: false,
    };
  },
  created() {
    this.$_showChannelActions = process.env.FEATURE_FLAGS.CHANNEL_ACTIONS;
  },
  watch: {
    conditionals: {
      deep: true,
      handler(newValue) {
        this.UPDATE_CONDITIONALS(newValue);
      },
    },
    selectedIntent: {
      deep: true,
      handler(newValue) {
        this.syncConditionalsFromIntent(newValue);
      },
    },
    formIntent: {
      deep: true,
      handler(newValue) {
        if (newValue && this.selectedIntent && newValue.id === this.selectedIntent.id) {
          this.syncConditionalsFromIntent(newValue, { checkForChanges: true });
        }
      },
    },
  },
  async mounted() {
    await Promise.all([
      this.$store.dispatch('dialogs/getEntityOperators'),
    ]);

    const id = parseInt(this.$route.params.id, 10);
    const intentId = id || get(this, 'items[0].id');

    if (intentId) {
      this.selectIntent(intentId);
    }

    Events.on('navigate.same.route', this.handleReloadPage.bind(this));
  },
  beforeUnmount() {
    Events.off('navigate.same.route', this.handleReloadPage.bind(this));
    return this.$store.dispatch('intents/resetIntents');
  },
  computed: {
    ...mapState({
      items: state => state.intents.items,
      entities: state => state.entities.entities,
      selectedIntent: state => state.intents.selected,
      formIntent: state => state.intents.formIntent,
    }),
    ...mapGetters([
      'pristine',
    ]),
    formActionList() {
      return this.formIntent.actions;
    },
    middleColumnsClassObj() {
      return {
        pristine: this.pristine,
      };
    },
    preconfiguredEntities() {
      return (this.$store.getters['entities/selectionList'] || []).map(e => e.name);
    },
    statusOptions() {
      return Object.values(STATUS_OPTIONS);
    },
    isSubmittable() {
      return !this.v$.$invalid;
    },
    isEditMode() {
      return !!(this.selectedIntent && this.formIntent);
    },
    isChanged() {
      return !this.pristine && this.isEditMode;
    },
  },
  methods: {
    ...mapMutations([
      'UPDATE_ACTIONS',
      'UPDATE_CONDITIONALS',
      'UPDATE_PAYLOAD',
      'DELETE_ACTION',
      'RESET_FORM',
      'UPDATE_ACTIVE',
      'UPDATE_NAME',
    ]),
    /**
     * Sync conditionals from an intent object to the local conditionals data
     * @param {Object|null} intent - The intent object to sync from
     * @param {Object} options - Options for syncing
     * @param {boolean} options.checkForChanges - If true, only sync if conditionals differ
     */
    syncConditionalsFromIntent(intent, options = {}) {
      if (!intent) {
        this.conditionals = {
          conditional_states: [],
          conditional_else_states: [],
          use_conditional_actions: false,
        };
        return;
      }

      const { conditional_states, conditional_else_states, use_conditional_actions } = intent;
      const newConditionals = {
        conditional_states: cloneDeep(conditional_states || []),
        conditional_else_states: cloneDeep(conditional_else_states || []),
        use_conditional_actions: use_conditional_actions || false,
      };

      // If checkForChanges is true, only sync if the conditionals differ
      if (options.checkForChanges) {
        const currentConditionals = {
          conditional_states: this.conditionals.conditional_states,
          conditional_else_states: this.conditionals.conditional_else_states,
          use_conditional_actions: this.conditionals.use_conditional_actions,
        };
        if (isEqual(newConditionals, currentConditionals)) {
          return;
        }
      }

      this.conditionals = newConditionals;
    },
    updateIntentStatus(row) {
      const intentData = cloneDeep(row);
      intentData.active = !intentData.active;
      return this.saveIntent(intentData);
    },
    openAddIntentDialog() {
      this.showAddIntentDialog = true;
    },
    closeAddIntentDialog() {
      this.showAddIntentDialog = false;
      this.newIntentName = '';
    },
    addNewIntent() {
      if (!this.newIntentName) {
        return Promise.reject(new Error(this.$t('iq_tools_tab.intents.save.name_required')));
      }

      const tempIntent = { name: this.newIntentName };
      return this.$store.dispatch('intents/addIntent', tempIntent)
        .then(data => {
          this.$aiq.notify.success(this.$t('iq_tools_tab.intents.intent_added'));
          this.newIntentName = '';

          // Close dialog
          this.closeAddIntentDialog();

          // Redirect to edit page
          this.selectIntent(data.data.id, true);
        }, error => {
          this.handleErrorMessage(error, tempIntent);
        });
    },
    deleteIntent(id) {
      const { name } = this.items.find(e => e.id === id);
      return this.$aiq.confirm(
        this.$t('iq_tools_tab.intents.delete.title'),
        this.$t('iq_tools_tab.intents.delete.confirm', { name }),
      ).then(() => {
        return this.$store.dispatch('intents/deleteIntent', id)
          .then(() => {
            this.$aiq.notify.success(this.$t('iq_tools_tab.intents.delete.intent_deleted'));
            return this.goToListPage();
          }).catch(error => {
            this.handleErrorMessage(error, { name });
          });
      });
    },
    saveIntent(intentData, disableSaveWarning) {
      return this.$store.dispatch('intents/updateIntent', intentData)
        .then(data => {
          if (this.selectedIntent) {
            this.selectIntent(data.data.id, disableSaveWarning);
          }
          this.$aiq.notify.success(this.$t('iq_tools_tab.intents.save.success'));

          return data;
        }).catch(err => {
          this.handleErrorMessage(err);
        });
    },
    saveFullIntent() {
      const preparedFormIntent = this.appendConditionalsUnitType(this.formIntent);
      return this.saveIntent(preparedFormIntent, true);
    },
    selectIntent(id, disableSaveWarning) {
      const dispatchSelectIntent = () => {
        return this.$store.dispatch('intents/selectIntent', id)
          .catch(() => this.$aiq.notify.error(this.$t('iq_tools_tab.intents.not_found')));
      };
      if (!this.pristine && this.selectedIntent && !disableSaveWarning) {
        const { title, content } = confirmUnsavedWarning;
        return this.$aiq.confirm(title, content)
          .then(dispatchSelectIntent);
      }
      return dispatchSelectIntent();
    },
    async goToListPage() {
      if (this.isChanged) {
        const { title, content } = confirmUnsavedWarning;
        try {
          await this.$aiq.confirm(title, content);
        } catch (e) {
          // user cancelled
          return;
        }
      }

      return await this.$store.dispatch('intents/resetSelected');
    },
    updateClass(newClass) {
      const payload = cloneDeep(this.formIntent.payload);
      payload.class = newClass;
      return this.UPDATE_PAYLOAD(payload);
    },
    // Methods - Other
    handleErrorMessage(err, tempIntent) {
      if (err.response.status === STATUS_CODES.CONFLICT && tempIntent) {
        this.$aiq.notify.error(this.$t('iq_tools_tab.intents.save.name_exists', { name: tempIntent.name }));
      } else if (err.response.data.name === 'INVALID_STRING_PATTERN') {
        this.$aiq.notify.error(this.$t('iq_tools_tab.intents.save.invalid_name', { reason: err.response.data.reason }));
      } else {
        this.$aiq.notify.error(this.$t('iq_tools_tab.intents.save.error'));
      }
    },
    deleteAction(index) {
      return this.DELETE_ACTION(index);
    },
    loadList({searchTerm, searchOffset}) {

      this.loading = true;

      const params = {
        q: searchTerm,
        limit: this.searchPageSize,
        offset: searchOffset,
      };

      if (this.getStatusFilter() !== null) {
        params.active = this.getStatusFilter();
      }

      return this.$store.dispatch('intents/getAll', params).then((response) => {
        this.loading = false;
        this.pagination = get(response, 'data.pagination', PAGINATION_DEFAULT);
      });
    },
    getStatusFilter() {
      if (this.filterStatus === STATUS_OPTIONS.ACTIVE) {
        return true;
      } else if (this.filterStatus === STATUS_OPTIONS.INACTIVE) {
        return false;
      }
      return null;
    },
    onEditCancel() {
      return this.$store.dispatch('intents/resetForm');
    },
    updateActions(evt) {
      const actions = cloneDeep(this.formActionList);

      if (evt.added) {
        // Putting in array to conform to appendUnitType
        const addedAction = actions[evt.added.newIndex];
        const action = [addedAction];
        appendUnitType(action);
      }

      this.UPDATE_ACTIONS(actions);
    },
    onActionItemClick(item) {
      const warningType = ['dialog', 'workflow'];
      if (!this.pristine && this.selectedIntent && warningType.includes(item.unit_type)) {
        const { title, content } = confirmUnsavedWarning;
        return this.$aiq.confirm(title, content)
          .then(() => this.$store.dispatch('routing/routeIqtoolActionItem', item));
      }
      else {
        return this.$store.dispatch('routing/routeIqtoolActionItem', item);
      }
    },
    appendConditionalsUnitType(formIntent) {
      const intent = cloneDeep(formIntent);
      intent.conditional_states = intent.conditional_states.map(s => ({
        ...s,
        actions: appendUnitType(s.actions),
      }));
      appendUnitType(intent.conditional_else_states);
      return intent;
    },
    updateIntentActive() {
      this.UPDATE_ACTIVE(!this.formIntent.active);
    },
    updateIntentName(newName) {
      this.UPDATE_NAME(newName);
    },
    handleReloadPage(pathInfo) {
      if (pathInfo.fullPath === this.$route.fullPath && this.selectedIntent) {
        return this.goToListPage();
      }
    },
    onSortByName(a, b) {
      const valA = a.name?.toLowerCase() || '';
      const valB = b.name?.toLowerCase() || '';

      if (valA === valB) {
        return 0;
      }

      return valA < valB ? 1 : -1;
    },
    onSortByStatus(a, b) {
      const valA = a.active ? 1 : 0;
      const valB = b.active ? 1 : 0;

      if (valA === valB) {
        return 0;
      }

      return valA < valB ? -1 : 1;
    },
  },
};